/**
 * In-house replacement for the abandoned `connected-react-router` package (last published 2022,
 * never updated for React 18/19). Reproduces its action types, action creators, reducer and
 * middleware behaviour exactly (verified against connected-react-router@6.8.0's source) so every
 * existing reducer/saga/selector reading `state.router` or dispatching `push`/`replace` keeps
 * working unchanged - only the import path changes.
 *
 * Not reproduced: connected-react-router's Redux DevTools time-travel sync (store.subscribe +
 * history.push to catch history up with a time-travelled store state). That's a dev-only
 * debugging aid, not used by any production code path here.
 */
export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
export const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';
/**
 * Adds a `query` object to a location, parsed from `location.search`. Matches
 * connected-react-router's `injectQuery` exactly, including leaving values un-decoded.
 */
export const injectQuery = (location) => {
    const search = location === null || location === void 0 ? void 0 : location.search;
    if (typeof search !== 'string' || search.length === 0) {
        return Object.assign(Object.assign({}, location), { query: {} });
    }
    const query = search
        .substring(1)
        .split('&')
        .reduce((acc, pair) => {
        const [key, value] = pair.split('=');
        acc[key] = value;
        return acc;
    }, {});
    return Object.assign(Object.assign({}, location), { query });
};
export const onLocationChanged = (location, action, isFirstRendering = false) => ({
    type: LOCATION_CHANGE,
    payload: { location: injectQuery(location), action, isFirstRendering },
});
const updateLocation = (method) => (...args) => ({
    type: CALL_HISTORY_METHOD,
    payload: { method, args },
});
export const push = updateLocation('push');
export const replace = updateLocation('replace');
export const go = updateLocation('go');
export const goBack = updateLocation('goBack');
export const goForward = updateLocation('goForward');
export const routerActions = { push, replace, go, goBack, goForward };
/**
 * Reducer factory (drop-in for connected-react-router's `connectRouter`). Skips the update on
 * the first dispatch (isFirstRendering) to avoid a double-render on init, same as upstream.
 */
export const connectRouter = (history) => {
    const initialState = {
        location: injectQuery(history.location),
        action: history.action,
    };
    return (state = initialState, action) => {
        if (action.type === LOCATION_CHANGE) {
            const { location, action: historyAction, isFirstRendering } = action.payload;
            return isFirstRendering ? state : { location, action: historyAction };
        }
        return state;
    };
};
/**
 * Middleware (drop-in for connected-react-router's `routerMiddleware`). Intercepts
 * CALL_HISTORY_METHOD actions, calls the matching `history` method, and swallows the action
 * (does not forward it to reducers/later middleware), matching upstream behaviour exactly.
 */
export const routerMiddleware = (history) => () => next => (action) => {
    if (action.type !== CALL_HISTORY_METHOD) {
        return next(action);
    }
    const { method, args } = action.payload;
    history[method](...args);
    return undefined;
};
